/* EstimateTab (DESIGN14 §F2.1 → DESIGN24 §T2.1) — the mini app's "Estimate"
   surface, rendered inside T1's ProjectScreen. Props are the FIXED contract
   from §F1.6: { projectId, architectureApproved, onPipelineChange }.

   DESIGN17 — FULL DOCUMENT COMPARTMENTALIZATION. The estimate is an
   ESTIMATOR-ONLY document end to end: every /api/architect/estimate route
   answers 403 to anyone without architect:estimate, so this surface exists
   for canEstimate accounts alone (estimator, root, non-confined dev).
   ProjectScreen already hides the tab for everyone else; the guard below is
   the defensive second lock — nothing renders and NO estimate request is
   made without canEstimate.

   DESIGN24 §T2.1 — the mini app now runs the WHOLE estimate cycle, not just
   a read-only view of it:
     • send for estimate (gated on an approved architecture) — unchanged;
     • the native document view, now EDITABLE: tap a module row → an edit
       sheet (name / description / days / integrations / TBD reason) → Save
       writes the whole spec back through PUT /:id/spec, exactly like the web
       document editor. ADD a module and DELETE a module are here too — the
       owner's web blocker, same capability on the phone;
     • the Ask / Edit AI chat (round 22) as a bottom sheet: a mode toggle,
       prose answers, and the SERVER-persisted thread via GET /:id/chat, so
       the conversation survives closing the mini app;
     • approve with the stage-1 approvals counter («Approved 1 of 2», the
       quiet «You approved» state, the muted «waiting for…» line) plus the
       optional knowledge-base feedback.
   Every write returns the server-authoritative estimate, which is adopted
   verbatim — the client never invents a total, a subtotal or a title.

   Generation is a tracked async RUN driven by the shared RunCard (resume via
   /runs/active on mount, Stop confirmed through the native Telegram dialog).
   The primary CTA rides the native Telegram MainButton; the document view is
   a full-screen overlay (an in-panel close button, so it never fights T1's
   BackButton). English, white-label throughout. */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  estimateApi,
  estimateAccess,
  EstimateMeta,
  EstimateFull,
  EstimateRun,
  EstimateSpec,
  EstimateCase,
  EstimateModule,
  EstimateDays,
} from '../../architect/api';
import { useAuth } from '../../auth/AuthContext';
import { fmtDate, modelDisplayEstimate } from '../../architect/utils';
import { parseEstimateDays as parseDays } from '../../architect/estimateDays';
import { useMainButton, useBackInterceptor, haptic, showConfirm } from '../telegram';
import { RunCard } from '../components/RunCard';
import { Sheet } from '../components/Sheet';
import { deliverFile, openInWeb } from '../files';
import {
  ApprovalMeter,
  ApprovalNote,
  ChatSheet,
  DocChatMessage,
  DocChatMode,
  canApproveNow,
  myApprovalTime,
  nextChatId,
  telemetryLine,
  useApprovalStatus,
  useApprovalStatuses,
} from '../components/DocSheet';
import '../tma-docs.css';
import { StageHeader, StageFooter, StageAiButton } from '../../architect/StageChrome';
import ApproveAsSelector, { useApprovalActor } from '../../architect/ApproveAsSelector';
import type { CommentAnchor } from '../../architect/api';
import { anchorQuote, anchorRevision, commentsAtBlock, CommentPinRef } from '../../architect/CommentAnchors';
import { useWorkspaceDraftGuard, useWorkspaceLeaveGuard } from '../../architect/useWorkspaceDraftGuard';
import { MessageCircle, MessageSquarePlus, Sparkles, Download, History, UserRound } from 'lucide-react';

export interface EstimateTabProps {
  projectId: string;
  /** Historical document behind a locked pipeline stage; viewing only. */
  readOnly?: boolean;
  /** Whether the project's architecture is approved — generation is gated on
      it for EVERY caller (the server enforces it too), so the MainButton is
      disabled with an inline hint until it flips true. Viewing stays open. */
  architectureApproved: boolean;
  /** Called after any action that can move the project's pipeline (a run
      lands, an estimate is edited or approved) so the parent refetches. */
  onPipelineChange: () => void;
  onCommentAnchor?: (anchor: CommentAnchor) => void;
  commentPins?: CommentPinRef[];
  onOpenCommentThread?: (id: string) => void;
  pickingComment?: boolean;
  jumpAnchor?: CommentAnchor | null;
  commentsOpen?: boolean;
  monitorCurrentRun?: boolean;
  /** Earlier documents belong to a prior architecture. Show a one-time
      recovery action instead of leaving the current step empty. */
  recoveryAllowed?: boolean;
  /** Server-owned run state closes the recovery path before RunCard resumes. */
  estimateRunning?: boolean;
}

// Wire ids are the API contract; the two tiers read Lite / Max (mirror of the
// web estimate panel). White-label — no provider names ever reach the user.
const ESTIMATE_MODELS = [
  { value: 'claude-opus-5-5', label: 'Lite' },
];

const STATUS_LABEL: Record<string, string> = {
  review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected', // legacy rows only — Reject is gone
};

const EDIT_PROMPTS = [
  'Add an e-signature module to Case 1',
  'Make the timelines more conservative',
  'Split the biggest case into two',
];

const ASK_PROMPTS = [
  'Why does Case 1 carry these days?',
  'What scope could be missing here?',
  'Which integrations are covered?',
];

/* ── server-side validation mirror (backend/src/estimate/validate.ts) ──
   Sending a spec the server will reject is a dead end on a phone, so the
   edit sheet refuses to save what would 400. These caps are the backend's. */
const CAP_MODULE_NAME = 120;
const CAP_DESC = 600;
const CAP_TBD_REASON = 300;
const MAX_APIS = 6;
const MAX_MODULES_PER_CASE = 40;
const DEFAULT_TEAM = '1 Middle Engineer';

/* ── day / total formatting (mirror of the editor rules) ── */

function numStr(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
}

type DayKind = 'num' | 'range' | 'tbd' | 'na';

function dayCell(d: EstimateDays | null | undefined): { text: string; kind: DayKind } {
  if (d === 'TBD') return { text: 'TBD', kind: 'tbd' };
  if (d === '–' || (d as unknown) === '-') return { text: '—', kind: 'na' };
  if (Array.isArray(d) && d.length === 2) {
    return { text: numStr(d[0]) + '–' + numStr(d[1]), kind: 'range' };
  }
  if (typeof d === 'number') return { text: numStr(d), kind: 'num' };
  return { text: '—', kind: 'na' };
}

function rangeText(min: number, max: number): string {
  return min === max ? numStr(min) : numStr(min) + '–' + numStr(max);
}

// days/5 rounded to the nearest 0.5 (mirrors the editor's fmtWeeks).
function weekStr(days: number): string {
  const w = Math.round((days / 5) * 2) / 2;
  return numStr(w);
}

/** A stored days value → the text the edit sheet shows. */
function daysToRaw(d: EstimateDays | null | undefined): string {
  if (d === 'TBD') return 'TBD';
  if (d === '–' || (d as unknown) === '-') return '–';
  if (Array.isArray(d) && d.length === 2) return numStr(d[0]) + '–' + numStr(d[1]);
  if (typeof d === 'number') return numStr(d);
  return '';
}

const isMarker = (d: EstimateDays | null): d is 'TBD' | '–' => d === 'TBD' || d === '–';

/* ── spec → the clean v2 payload PUT /:id/spec validates ──
   Computed fields (subtotal / total / total_label / title) are NEVER sent —
   the server recomputes and overwrites them. The fallbacks below only ever
   fire on a spec that arrived without a required string; they keep an edit
   from bouncing as «invalid document» over a field the phone never showed. */
function cleanSpec(spec: EstimateSpec, cases: EstimateCase[]): EstimateSpec {
  const client = (spec.client || '').trim();
  const system = (spec.system || '').trim();
  return {
    version: 2,
    client: client || system || 'Client',
    system,
    cases: cases.map((c, i) => ({
      name: (c.name || '').trim() || 'Case ' + (i + 1),
      modules: (c.modules || []).map((m): EstimateModule => {
        const raw = (m.days as unknown) === '-' ? '–' : m.days;
        const days: EstimateDays = raw == null ? '–' : (raw as EstimateDays);
        const out: EstimateModule = {
          name: (m.name || '').trim(),
          description: (m.description || '').trim(),
          apis: (m.apis || []).map((a) => (a || '').trim()).filter(Boolean).slice(0, MAX_APIS),
          days,
        };
        if (isMarker(days)) {
          out.tbd_reason = (m.tbd_reason || '').trim() || 'To be scoped';
        }
        return out;
      }),
    })),
    notes: (spec.notes || []).map((s) => (s || '').trim()).filter(Boolean),
    summary: {
      deliverables:
        (spec.summary?.deliverables || '').trim() ||
        system ||
        client ||
        'Development plan',
      team: (spec.summary?.team || '').trim() || DEFAULT_TEAM,
    },
  };
}

/* ── the module edit sheet's draft ── */
interface ModuleDraft {
  caseIdx: number;
  /** null ⇒ a NEW module appended to that case. */
  moduleIdx: number | null;
  name: string;
  description: string;
  apisRaw: string;
  daysRaw: string;
  tbdReason: string;
}

function draftFromModule(caseIdx: number, moduleIdx: number, m: EstimateModule): ModuleDraft {
  return {
    caseIdx,
    moduleIdx,
    name: m.name || '',
    description: m.description || '',
    apisRaw: (m.apis || []).join(', '),
    daysRaw: daysToRaw(m.days),
    tbdReason: m.tbd_reason || '',
  };
}

function emptyDraft(caseIdx: number): ModuleDraft {
  return {
    caseIdx,
    moduleIdx: null,
    name: '',
    description: '',
    apisRaw: '',
    daysRaw: '1',
    tbdReason: '',
  };
}

export function EstimateTab({
  projectId,
  readOnly: archived = false,
  architectureApproved,
  onPipelineChange,
  onCommentAnchor,
  commentPins = [],
  onOpenCommentThread,
  pickingComment,
  jumpAnchor,
  commentsOpen,
  monitorCurrentRun,
  recoveryAllowed = false,
  estimateRunning = false,
}: EstimateTabProps) {
  const { user, permissions } = useAuth();
  // The estimate surface remains role-gated even if a caller mounts it directly.
  const { canEstimate, canViewEstimate, canApproveEstimate } = useMemo(
    () => estimateAccess(user, permissions),
    [user, permissions],
  );

  const readOnly = archived || !canEstimate;
  const requiresApprovalStatus = !!user?.stage_access?.estimate?.configured;

  // null while the first load is in flight (drives the skeletons).
  const [estimates, setEstimates] = useState<EstimateMeta[] | null>(null);
  const [estimateListCheckedFor, setEstimateListCheckedFor] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [runError, setRunError] = useState<string | null>(null);
  const [pendingRun, setPendingRun] = useState<EstimateRun | null>(null);
  const [runBusy, setRunBusy] = useState(false);
  const [runResumeChecked, setRunResumeChecked] = useState(false);
  // A one-line list-level confirmation (e.g. «the price sheet is ready») —
  // the viewer closes on a final approve, so a viewer-scoped note would die
  // with it before anyone read it.
  const [listNote, setListNote] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);

  // Native document viewer (full-screen overlay).
  const [viewerId, setViewerId] = useState<string | null>(null);
  const [viewer, setViewer] = useState<EstimateFull | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerNote, setViewerNote] = useState<string | null>(null);

  // Approve feedback drawer (estimator, review status).
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [approving, setApproving] = useState(false);

  // Document editing (DESIGN24 §T2.1).
  const [draft, setDraft] = useState<ModuleDraft | null>(null);
  const [savingSpec, setSavingSpec] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [approvalActorOpen, setApprovalActorOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // AI chat (round 22) — server-persisted thread.
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<DocChatMessage[]>([]);
  const [chatBusy, setChatBusy] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatModel, setChatModel] = useState(ESTIMATE_MODELS[0].value);
  const [chatDraft, setChatDraft] = useState({ dirty: false, busy: false });
  const originalModule = draft && viewer && draft.moduleIdx != null
    ? viewer.spec.cases?.[draft.caseIdx]?.modules?.[draft.moduleIdx] : undefined;
  const originalDraft = draft && originalModule && draft.moduleIdx != null
    ? draftFromModule(draft.caseIdx, draft.moduleIdx, originalModule)
    : draft ? emptyDraft(draft.caseIdx) : null;
  const editingDirty = !!draft && JSON.stringify(draft) !== JSON.stringify(originalDraft);
  useWorkspaceDraftGuard(() => ({ dirty: editingDirty || !!feedback.trim() || chatDraft.dirty,
    busy: savingSpec || approving || chatBusy || chatDraft.busy }),
    { onBusy: () => setViewerError('Finish saving or sending before leaving this estimate.') });
  const requestLeave = useWorkspaceLeaveGuard();

  // Bumped after anything that can move the approvals (a vote, a spec edit —
  // the server clears the votes collected for the OLD content).
  const [apprRev, setApprRev] = useState(0);

  const load = useCallback(
    async (quiet = false) => {
      try {
        const r = await estimateApi.listEstimates(projectId);
        setEstimates(r.estimates || []);
        setEstimateListCheckedFor(projectId);
        if (!quiet) setLoadError(null);
      } catch (err: unknown) {
        setEstimateListCheckedFor(null);
        if (!quiet) setLoadError((err as Error).message || 'Could not load estimates');
        setEstimates((prev) => prev || []);
      }
    },
    [projectId],
  );

  // Never fire the list call without the sub-role — it is a 403 now.
  useEffect(() => {
    if (canViewEstimate) void load();
  }, [canViewEstimate, load]);
  useEffect(() => { setRunResumeChecked(false); }, [projectId]);

  /* ── approvals (DESIGN23 stage 1) ────────────────────────
     One status for the open document (that is where Approve lives) and a
     compact counter on every in-review row. A failed read means «no policy»,
     which is exactly the pre-stage-1 behaviour. */
  const approvalIds = useMemo(
    () => (estimates || []).map((e) => e.id),
    [estimates],
  );
  const rowApprovals = useApprovalStatuses(
    'estimate',
    'estimate',
    approvalIds,
    canViewEstimate,
    apprRev,
  );
  const apprStatus = useApprovalStatus(
    'estimate',
    'estimate',
    viewer ? viewer.id : null,
    canViewEstimate,
    apprRev,
  );
  const approvalActor = useApprovalActor({ root: !!user?.isRoot, status: apprStatus, step: 'estimate',
    targetId: viewerId || projectId, enabled: !archived && viewer?.status === 'review' });
  const approvalBody = approvalActor.body;
  const selectApprovalActor = approvalActor.select;
  const approvalAllowed = user?.isRoot ? approvalActor.canApprove : canApproveNow(apprStatus) && !myApprovalTime(apprStatus, user?.id);

  const onRunDone = useCallback(
    (run: EstimateRun) => {
      setPendingRun(null);
      setRunError(null);
      void load(true);
      if (run.estimate_id) {
        const id = run.estimate_id;
        setFlashId(id);
        window.setTimeout(() => setFlashId((cur) => (cur === id ? null : cur)), 1600);
      }
      onPipelineChange();
    },
    [load, onPipelineChange],
  );

  const onRunError = useCallback((message: string) => {
    setPendingRun(null);
    setRunError(message);
    onPipelineChange();
  }, [onPipelineChange]);

  const startRecovery = useCallback(async () => {
    if (!canEstimate || !architectureApproved || (!recoveryAllowed && readOnly) || runBusy || estimateRunning) return;
    haptic.impact('medium');
    setRunError(null);
    try {
      const { run } = await estimateApi.generateEstimate(projectId, ESTIMATE_MODELS[0].value);
      setPendingRun(run);
      onPipelineChange();
    } catch (err: unknown) {
      haptic.notify('error');
      setRunError((err as Error).message || 'Could not start the estimate.');
      onPipelineChange();
    }
  }, [canEstimate, architectureApproved, recoveryAllowed, readOnly, runBusy, estimateRunning, projectId, onPipelineChange]);

  /* ── native document viewer ─────────────────────────────── */
  const openViewer = useCallback(async (est: EstimateMeta) => {
    haptic.impact('light');
    setViewerId(est.id);
    setViewer(null);
    setViewerError(null);
    setViewerNote(null);
    setViewerLoading(true);
    setFeedbackOpen(false);
    setFeedback('');
    setDraft(null);
    setMenuOpen(false);
    setChatOpen(false);
    setChatMessages([]);
    try {
      const { estimate } = await estimateApi.getEstimate(est.id);
      // Guard against a viewer closed/switched while the GET was in flight.
      setViewerId((cur) => {
        if (cur === est.id) setViewer(estimate);
        return cur;
      });
    } catch (err: unknown) {
      setViewerError((err as Error).message || 'Could not open the document');
    } finally {
      setViewerLoading(false);
    }
  }, []);
  const autoOpened = React.useRef(false);
  useEffect(() => {
    // An older document is history, not the current estimate. Leave the
    // recovery action visible instead of opening that old document on entry.
    if (autoOpened.current || archived || recoveryAllowed || !estimates?.length) return;
    autoOpened.current = true;
    void openViewer(estimates[0]);
  }, [estimates, openViewer, archived, recoveryAllowed]);
  useEffect(() => {
    if (!jumpAnchor || !estimates?.length) return;
    const target = estimates.find(item => item.id === jumpAnchor.document_id);
    if (target && viewerId !== target.id) void openViewer(target);
  }, [jumpAnchor, estimates, viewerId, openViewer]);
  useEffect(() => {
    if (!jumpAnchor || viewer?.id !== jumpAnchor.document_id) return;
    const timeout = window.setTimeout(() => {
      const target = Array.from(document.querySelectorAll<HTMLElement>('[data-tma-anchor-path]')).find(node => node.dataset.tmaAnchorPath === jumpAnchor.path);
      target?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      if (target) { target.classList.add('tma-anchor-flash'); window.setTimeout(() => target.classList.remove('tma-anchor-flash'), 1800); }
    }, 120);
    return () => window.clearTimeout(timeout);
  }, [jumpAnchor, viewer]);

  const closeViewer = useCallback(() => {
    haptic.impact('light');
    setViewerId(null);
    setViewer(null);
    setViewerError(null);
    setViewerNote(null);
    setFeedbackOpen(false);
    setFeedback('');
    setDraft(null);
    setMenuOpen(false);
    setChatOpen(false);
    setChatMessages([]);
  }, []);

  /* ── the persisted chat thread (GET /:id/chat) ───────────
     Loaded once the document is open so tapping «AI» is instant, and reset
     whenever the open document changes. A failure is silent: the thread is a
     convenience, never a blocker. */
  const chatDocId = viewer?.id || null;
  useEffect(() => {
    if (!canViewEstimate || !chatDocId) return undefined;
    let alive = true;
    setChatMessages([]);
    setChatLoading(true);
    estimateApi
      .getEstimateChat(chatDocId)
      .then((r) => {
        if (!alive) return;
        const restored = (r?.messages || [])
          .map((m): DocChatMessage | null => {
            const text = String(m.content || '').trim();
            if (!text) return null;
            const mode: DocChatMode = m.mode === 'ask' ? 'ask' : 'edit';
            if (m.role === 'user') {
              return { id: 'srv' + m.id, role: 'user', mode, text };
            }
            if (m.role !== 'assistant') return null;
            return {
              id: 'srv' + m.id,
              role: 'assistant',
              mode,
              text,
              meta: telemetryLine(m.telemetry),
            };
          })
          .filter((m): m is DocChatMessage => m !== null);
        if (restored.length) setChatMessages(restored);
      })
      .catch(() => {
        /* no stored thread — start empty */
      })
      .finally(() => {
        if (alive) setChatLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [canViewEstimate, chatDocId]);

  const pushChat = useCallback((m: DocChatMessage) => {
    setChatMessages((xs) => [...xs, m].slice(-200));
  }, []);

  /* ── document writes (PUT /:id/spec) ────────────────────
     Every module add / edit / delete sends the WHOLE document, exactly like
     the web editor's Save: the server re-validates, recomputes the totals,
     re-renders the .docx, pushes the status back to 'review' and clears the
     votes that were cast for the old content. The response is adopted
     verbatim — no client-side total ever reaches the screen. */
  const saveCases = useCallback(
    async (cases: EstimateCase[], okMessage: string): Promise<boolean> => {
      const cur = viewer;
      if (!cur || cur.status === 'approved' || readOnly || savingSpec) return false;
      setSavingSpec(true);
      setViewerError(null);
      try {
        const { estimate } = await estimateApi.saveEstimateSpec(
          cur.id,
          cleanSpec(cur.spec, cases),
        );
        setViewer(estimate);
        setViewerNote(okMessage);
        haptic.notify('success');
        setApprRev((n) => n + 1);
        await load(true);
        onPipelineChange();
        return true;
      } catch (err: unknown) {
        haptic.notify('error');
        setViewerError((err as Error).message || 'Could not save the document');
        return false;
      } finally {
        setSavingSpec(false);
      }
    },
    [viewer, readOnly, savingSpec, load, onPipelineChange],
  );

  const saveDraft = useCallback(async () => {
    const d = draft;
    const cur = viewer;
    if (!d || !cur || cur.status === 'approved' || readOnly) return;
    const days = parseDays(d.daysRaw);
    const name = d.name.trim();
    const description = d.description.trim();
    if (!name || !description || days == null) return;
    if (isMarker(days) && !d.tbdReason.trim()) return;

    const module: EstimateModule = {
      name: name.slice(0, CAP_MODULE_NAME),
      description: description.slice(0, CAP_DESC),
      apis: d.apisRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, MAX_APIS),
      days,
    };
    if (isMarker(days)) module.tbd_reason = d.tbdReason.trim().slice(0, CAP_TBD_REASON);

    const cases = (cur.spec.cases || []).map((c, i) => {
      if (i !== d.caseIdx) return c;
      const modules = [...(c.modules || [])];
      if (d.moduleIdx == null) modules.push(module);
      else modules[d.moduleIdx] = module;
      return { ...c, modules };
    });
    const ok = await saveCases(
      cases,
      d.moduleIdx == null ? 'Module added — document re-rendered' : 'Saved — document re-rendered',
    );
    if (ok) setDraft(null);
  }, [draft, viewer, readOnly, saveCases]);

  const deleteModule = useCallback(
    async (caseIdx: number, moduleIdx: number) => {
      const cur = viewer;
      if (!cur || cur.status === 'approved' || readOnly) return;
      const cases = cur.spec.cases || [];
      const target = cases[caseIdx];
      if (!target) return;
      if ((target.modules || []).length <= 1) {
        haptic.notify('warning');
        setViewerError(
          'Each case needs at least one module — edit this one instead of deleting it.',
        );
        return;
      }
      haptic.impact('medium');
      const label = (target.modules || [])[moduleIdx]?.name || 'this module';
      const ok = await showConfirm('Delete “' + label + '” from the estimate?');
      if (!ok) return;
      const next = cases.map((c, i) =>
        i === caseIdx
          ? { ...c, modules: (c.modules || []).filter((_, j) => j !== moduleIdx) }
          : c,
      );
      const done = await saveCases(next, 'Module deleted — document re-rendered');
      if (done) setDraft(null);
    },
    [viewer, readOnly, saveCases],
  );

  /* ── AI chat send (edit → /prompt, ask → /ask) ─────────── */
  const chatSend = useCallback(
    async (text: string, mode: DocChatMode) => {
      const cur = viewer;
      if (!canEstimate || !cur || chatBusy || (readOnly && !(cur.status === 'approved' && mode === 'ask')) || (cur.status === 'approved' && mode === 'edit')) return;
      pushChat({ id: nextChatId(), role: 'user', mode, text });
      setChatBusy(true);
      try {
        if (mode === 'ask') {
          const r = await estimateApi.askEstimate(cur.id, text, chatModel);
          pushChat({
            id: nextChatId(),
            role: 'assistant',
            mode: 'ask',
            text:
              String(r?.answer || '').trim() ||
              'No answer came back — try rephrasing the question.',
            meta: telemetryLine(r?.telemetry),
          });
        } else {
          const r = await estimateApi.promptEstimate(cur.id, {
            instruction: text,
            model: chatModel,
          });
          // Adopt the server-authoritative document, exactly like a save.
          setViewer(r.estimate);
          setViewerNote('Document updated — status back to review');
          setApprRev((n) => n + 1);
          const total = r.estimate.spec?.total;
          pushChat({
            id: nextChatId(),
            role: 'assistant',
            mode: 'edit',
            text:
              'Document updated — status back to review.' +
              (total
                ? ' Total: ' + rangeText(total.min, total.max) + ' days.'
                : ''),
            meta: telemetryLine(r?.telemetry),
          });
          haptic.notify('success');
          await load(true);
          onPipelineChange();
        }
      } catch (err: unknown) {
        haptic.notify('error');
        pushChat({
          id: nextChatId(),
          role: 'error',
          mode,
          text: (err as Error).message || 'The assistant could not finish — try again',
          retry: { text, mode },
        });
      } finally {
        setChatBusy(false);
      }
    },
    [canEstimate, viewer, chatBusy, readOnly, chatModel, pushChat, load, onPipelineChange],
  );

  const chatClear = useCallback(async () => {
    const cur = viewer;
    if (!canEstimate || !cur || chatBusy) return;
    const ok = await showConfirm('Clear this conversation?');
    if (!ok) return;
    try {
      await estimateApi.clearEstimateChat(cur.id);
      setChatMessages([]);
    } catch (err: unknown) {
      pushChat({
        id: nextChatId(),
        role: 'system',
        text: (err as Error).message || 'The history could not be cleared — try again',
      });
    }
  }, [canEstimate, viewer, chatBusy, pushChat]);

  /* ── download the stored .docx (DESIGN47) ─────────────────
     A fetch()ed blob cannot be saved inside Telegram's webview (<a download>
     is inert there), so the document goes out as a SHORT-LIVED SIGNED LINK
     handed to Telegram's own downloader or the system browser. */
  const download = useCallback(async () => {
    const cur = viewer;
    if (!cur || downloading) return;
    setDownloading(true);
    try {
      const how = await deliverFile('estimate_docx', cur.id);
      haptic.notify('success');
      setViewerNote(how === 'telegram' ? 'Telegram is saving the document' : how === 'declined' ? 'Download cancelled' : 'The document opens in your browser');
    } catch (err: unknown) {
      haptic.notify('error');
      setViewerError((err as Error).message || 'Could not prepare the document');
    } finally {
      setDownloading(false);
    }
  }, [viewer, downloading]);

  /* ── approve — the ONLY estimate decision ────────────────
     DESIGN23: with a configured multi-approver policy the server records the
     vote and deliberately LEAVES the estimate in 'review' until the threshold
     is met; `approvals` says how far it got. With no policy the first
     approval flips it, exactly as before. */
  const approve = useCallback(async () => {
    if (!canApproveEstimate || archived || !viewer || viewer.status !== 'review' || approving || savingSpec || editingDirty || (requiresApprovalStatus && !apprStatus) || !approvalAllowed) return;
    const note = feedback.trim();
    setApproving(true);
    setViewerError(null);
    try {
      const r = approvalBody.approve_as_user_id
        ? await estimateApi.approveEstimate(viewer.id, note || undefined, approvalBody)
        : await estimateApi.approveEstimate(viewer.id, note || undefined);
      selectApprovalActor('');
      const progress = r?.approvals;
      const met = !progress || progress.given >= progress.required;
      haptic.notify('success');
      setFeedbackOpen(false);
      setFeedback('');
      if (met) {
        setViewerId(null);
        setViewer(null);
        // DESIGN28/30 — the approval derives the project price sheet; the
        // pipeline moves on to Pricing (root's step), and saying so here is
        // the only signal an estimator gets that their part is done.
        if (r?.auto_started === 'pricing') {
          setListNote('Approved — the price sheet is ready for pricing.');
        }
      } else {
        setViewerNote(
          'Your approval is recorded — ' + progress!.given + ' of ' + progress!.required,
        );
      }
      await load(true);
      onPipelineChange();
    } catch (err: unknown) {
      haptic.notify('error');
      setViewerError((err as Error).message || 'Could not approve');
    } finally {
      setApproving(false);
      // Refresh the counters either way — a «not an approver» refusal is
      // itself news this document should reflect.
      setApprRev((n) => n + 1);
    }
  }, [canApproveEstimate, archived, requiresApprovalStatus, apprStatus, approvalAllowed, approvalBody, selectApprovalActor, viewer, approving, savingSpec, editingDirty, feedback, load, onPipelineChange]);

  /* ── the single native MainButton, state-driven ─────────── */
  const immutable = viewer?.status === 'approved';
  const documentReadOnly = readOnly || immutable;
  const pendingApproval = !!(viewer && viewer.status === 'review');
  const showApprove = canApproveEstimate && !archived && (!requiresApprovalStatus || !!apprStatus) && pendingApproval && approvalAllowed;
  // The native button sits below the webview and would peek under a sheet.
  const sheetOpen = feedbackOpen || chatOpen || menuOpen || approvalActorOpen || !!draft || !!commentsOpen;
  useBackInterceptor(!!viewerId && !sheetOpen, () => requestLeave(closeViewer));
  const hiddenMb = { text: 'Approve', visible: false, onClick: () => {} };
  useMainButton(hiddenMb);

  /* ── DESIGN17 self-guard: no estimate sub-role ⇒ render nothing ──
     ProjectScreen already hides the tab; this is the defensive second lock
     so the surface can never be reached (and no request fired) by a
     generator- or poster-only account. Every hook above already ran, and
     the MainButton state above resolves to hidden for them. */
  if (!canViewEstimate) return null;

  const list = estimates || [];
  const showRecovery = canEstimate && architectureApproved && estimates !== null && estimateListCheckedFor === projectId &&
    (recoveryAllowed || (!readOnly && list.length === 0)) &&
    !estimateRunning && !runBusy && runResumeChecked && !pendingRun && !viewerId;
  const editable = !!viewer && Array.isArray(viewer.spec?.cases) && !!viewer.spec.cases.length;
  const draftValid = (() => {
    if (!draft) return false;
    const d = parseDays(draft.daysRaw);
    if (d == null) return false;
    if (isMarker(d) && !draft.tbdReason.trim()) return false;
    return !!draft.name.trim() && !!draft.description.trim();
  })();
  const draftCase =
    draft && viewer ? (viewer.spec.cases || [])[draft.caseIdx] : undefined;
  const caseFull =
    !!draftCase && draft?.moduleIdx == null &&
    (draftCase.modules || []).length >= MAX_MODULES_PER_CASE;

  return (
    <div className={'tma-doc-tab stage-mini-surface' + (viewerId ? ' has-viewer' : '')}>
      {!viewerId ? <StageHeader title="Estimate" status={list.length ? 'Version history' : 'Not started'} actions={list[0] ? [{ id: 'latest', label: 'Open latest estimate', icon: <History size={17}/>, onClick: () => { void openViewer(list[0]); } }] : []}/> : null}
      {/* Resumes an in-flight run on mount; invisible when idle. */}
      {(!archived || monitorCurrentRun || recoveryAllowed) ? <RunCard<EstimateRun>
        projectId={projectId}
        run={pendingRun}
        poll={estimateApi.getEstimateRun}
        canCancel={canEstimate && !archived}
        cancel={estimateApi.cancelEstimateRun}
        getActive={estimateApi.getActiveEstimateRun}
        heading="Preparing the estimate"
        modelLabel={modelDisplayEstimate}
        steps={['Prepare sources', 'AI plans the work', 'Validate', 'Render', 'Save']}
        onDone={onRunDone}
        onError={onRunError}
        onBusyChange={(busy) => { setRunBusy(busy); if (!busy) setPendingRun(null); }}
        onResumeChecked={() => setRunResumeChecked(true)}
      /> : null}

      {showRecovery ? (
        <div className="tma-doc-recovery" role="group" aria-label="Generate current estimate">
          <span className="tma-doc-recovery-icon"><Sparkles size={22} aria-hidden="true" /></span>
          <strong>No estimate for this architecture yet</strong>
          <p>Generate the current estimate to continue to Pricing.</p>
          <button type="button" className="tma-doc-btn tma-doc-btn-primary tma-doc-btn-block" onClick={startRecovery}>{runError ? 'Retry estimate' : 'Generate estimate'}</button>
        </div>
      ) : null}

      {listNote ? (
        <div className="tma-doc-banner ok" role="status">
          {listNote}
        </div>
      ) : null}
      {runError ? (
        <div className="tma-doc-banner err" role="alert">
          {runError}
        </div>
      ) : null}
      {loadError ? (
        <div className="tma-doc-banner err" role="alert">
          {loadError}
        </div>
      ) : null}

      {/* List — every row, every status: this surface is estimator-only now,
          so there is no filtered/"waiting for review" variant left. */}
      {estimates === null ? (
        <div className="tma-doc-list">
          {[0, 1, 2].map((i) => (
            <div className="tma-doc-row tma-doc-row-skel" key={i}>
              <div className="tma-doc-skel tma-doc-skel-a" />
              <div className="tma-doc-skel tma-doc-skel-b" />
            </div>
          ))}
        </div>
      ) : recoveryAllowed ? (
        estimateRunning || runBusy || pendingRun ? null : showRecovery ? null : <div className="tma-doc-empty-sub">Checking the current estimate…</div>
      ) : list.length ? (
        <div className="tma-doc-list">
          {list.map((est) => (
            <button
              type="button"
              key={est.id}
              className={'tma-doc-row' + (est.id === flashId ? ' flash' : '')}
              onClick={() => openViewer(est)}
            >
              <div className="tma-doc-row-head">
                <span className={'tma-doc-pill ' + est.status}>
                  {STATUS_LABEL[est.status] || est.status}
                </span>
                <span className="tma-doc-model">{modelDisplayEstimate(est.model)}</span>
                <span className="tma-doc-date">{fmtDate(est.created_at)}</span>
              </div>
              <div className="tma-doc-row-title">{est.title || 'Estimate'}</div>
              {/* Reviewer NAME is root-only; everyone else sees the acceptance
                  fact + WHEN. */}
              {est.reviewed_by_name ? (
                <div className="tma-doc-row-meta">
                  {(est.status === 'approved' ? 'Approved by ' : 'Reviewed by ')}
                  {est.reviewed_by_name}
                  {est.reviewed_at ? ' · ' + fmtDate(est.reviewed_at) : ''}
                </div>
              ) : est.reviewed_at ? (
                <div className="tma-doc-row-meta">
                  {'Accepted · ' + fmtDate(est.reviewed_at)}
                </div>
              ) : null}
              {est.review_note ? (
                <div className="tma-doc-row-note">“{est.review_note}”</div>
              ) : null}
              {/* «Approved 1 of 2» — drawn only when it says something. */}
              <ApprovalMeter
                status={rowApprovals[est.id]}
                pending={est.status === 'review'}
              />
              <span className="tma-doc-row-open" aria-hidden="true">
                Open ›
              </span>
            </button>
          ))}
        </div>
      ) : showRecovery ? null : (
        <div className="tma-doc-empty">
          <div className="tma-doc-empty-emoji" aria-hidden="true">
            🧮
          </div>
          <div className="tma-doc-empty-title">No estimates yet</div>
          <div className="tma-doc-empty-sub">
            {architectureApproved
              ? 'The estimate starts automatically after Architecture approval. It will appear here when ready.'
              : 'Approve the architecture to unlock the estimate.'}
          </div>
        </div>
      )}

      <div className="tma-doc-mb-spacer" />
      {!viewerId ? <>
        {canEstimate ? <StageAiButton mini disabled={!list[0]} onClick={() => { if (list[0]) { void openViewer(list[0]); setChatOpen(true); } }}/> : null}
        <StageFooter stage="estimate" className="stage-mini-footer" pending={list[0]?.status !== 'approved'} status={list[0] ? rowApprovals[list[0].id] : undefined} title={list.length ? 'Version history' : 'Estimate not started'}/>
      </> : null}

      {/* Native full-screen document viewer */}
      {viewerId ? (
        <div className="tma-doc-viewer" aria-label="Estimate document">
          <StageHeader title="Estimate" status={immutable ? 'Approved' : savingSpec ? 'Saving…' : 'In review'} actions={[
            { id: 'versions', label: 'Version history', icon: <History size={17}/>, onClick: closeViewer },
            ...(user?.isRoot && canApproveEstimate && !archived && pendingApproval ? [{ id: 'approve-as', label: 'Approve as', icon: <UserRound size={17}/>, onClick: () => setApprovalActorOpen(true) }] : []),
            ...(viewer?.has_docx ? [{ id: 'download', label: 'Download DOCX', icon: <Download size={17}/>, disabled: downloading, onClick: () => { void download(); } }] : []),
            ...(showApprove ? [{ id: 'feedback', label: 'Add approval feedback', icon: <MessageCircle size={17}/>, onClick: () => { setViewerError(null); setFeedbackOpen(true); } }] : []),
          ]}/>

          <div className="tma-doc-viewer-body">
            {viewerError && !(draft && !documentReadOnly) && !(feedbackOpen && showApprove) ? (
              <div
                className="tma-doc-banner err"
                role="alert"
                onClick={() => setViewerError(null)}
              >
                {viewerError}
              </div>
            ) : null}
            {viewerNote ? (
              <div
                className="tma-doc-banner ok"
                role="status"
                onClick={() => setViewerNote(null)}
              >
                {viewerNote}
              </div>
            ) : null}

            {viewerLoading || !viewer ? (
              !viewerError ? (
                <div className="tma-doc-loading">
                  <span className="tma-spin tma-doc-loading-spin" />
                  <span>Opening document…</span>
                </div>
              ) : null
            ) : (
              <>
                {savingSpec ? (
                  <div className="tma-doc-saving" role="status">
                    <span className="tma-spin tma-doc-saving-spin" />
                    Saving the document…
                  </div>
                ) : null}


                <EstimateDocument
                  spec={viewer.spec}
                  documentId={viewer.id}
                  status={viewer.status}
                  editable={editable && !savingSpec && !documentReadOnly}
                  onCommentAnchor={onCommentAnchor}
                  commentPins={commentPins}
                  onOpenCommentThread={onOpenCommentThread}
                  pickingComment={pickingComment}
                  onEditModule={(ci, mi) => {
                    haptic.impact('light');
                    setViewerError(null);
                    const m = (viewer.spec.cases || [])[ci]?.modules?.[mi];
                    if (m) setDraft(draftFromModule(ci, mi, m));
                  }}
                  onAddModule={(ci) => {
                    haptic.impact('light');
                    setViewerError(null);
                    setDraft(emptyDraft(ci));
                  }}
                />

              </>
            )}
            <div className="tma-doc-mb-spacer" />
          </div>
          {viewer ? <>
            {canEstimate ? <StageAiButton mini open={chatOpen} busy={chatBusy} disabled={viewerLoading || (readOnly && !immutable)} onClick={() => { haptic.select(); setChatOpen(value => !value); }}/> : null}
            <StageFooter className="stage-mini-footer" stage="estimate" pending={!immutable} status={apprStatus} userId={user?.id} title={immutable ? 'Estimate approved' : 'Ready for review'}>
              {showApprove ? <button type="button" className="arch-btn arch-btn-primary" disabled={approving || savingSpec || sheetOpen} onClick={() => { haptic.impact('medium'); void approve(); }}>{approving ? 'Approving…' : 'Approve estimate'}</button> : null}
            </StageFooter>
          </> : null}
        </div>
      ) : null}

      {/* ⋯ document actions */}
      <Sheet open={!!user?.isRoot && canApproveEstimate && !archived && pendingApproval && approvalActorOpen} onClose={() => setApprovalActorOpen(false)} title="Approve as"
        footer={<button type="button" className="tma-btn tma-btn-primary" onClick={() => setApprovalActorOpen(false)}>Done</button>}>
        <ApproveAsSelector actor={approvalActor} disabled={approving || savingSpec || editingDirty}/>
      </Sheet>
      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Document">
        {user?.isRoot && canApproveEstimate && !archived && pendingApproval ? <button type="button" className="tma-menu-row" onClick={() => { setMenuOpen(false); setApprovalActorOpen(true); }}>
          <span className="tma-menu-row-body"><span className="tma-menu-row-title">Approve as</span></span>
        </button> : null}
        {showApprove ? <button type="button" className="tma-menu-row" onClick={() => { setMenuOpen(false); setViewerError(null); setFeedbackOpen(true); }}>
          <span className="tma-menu-row-body">
            <span className="tma-menu-row-title">Add approval feedback</span>
            <span className="tma-menu-row-sub">Optional note for the knowledge base before approving</span>
          </span>
        </button> : null}
        {(canEstimate && (!readOnly || immutable)) ? <button
          type="button"
          className="tma-menu-row"
          onClick={() => {
            setMenuOpen(false);
            setChatOpen(true);
          }}
        >
          <span className="tma-menu-row-emoji">✦</span>
          <span className="tma-menu-row-body">
            <span className="tma-menu-row-title">{immutable ? 'Ask Lato AI' : 'Ask or edit with AI'}</span>
            <span className="tma-menu-row-sub">
              {immutable ? 'Questions only · this approved document cannot change' : 'Ask a question, or describe a change in plain English'}
            </span>
          </span>
        </button> : null}
        {viewer?.has_docx ? (
          <button
            type="button"
            className="tma-menu-row"
            disabled={downloading}
            onClick={() => {
              setMenuOpen(false);
              void download();
            }}
          >
            <span className="tma-menu-row-emoji">⬇︎</span>
            <span className="tma-menu-row-body">
              <span className="tma-menu-row-title">
                {downloading ? 'Preparing…' : 'Download .docx'}
              </span>
              <span className="tma-menu-row-sub">The last saved document</span>
            </span>
          </button>
        ) : null}
        {!readOnly ? <button
          type="button"
          className="tma-menu-row"
          onClick={() => {
            setMenuOpen(false);
            void openInWeb(projectId, 'estimate');
          }}
        >
          <span className="tma-menu-row-emoji">🖥</span>
          <span className="tma-menu-row-body">
            <span className="tma-menu-row-title">Open in the web app</span>
            <span className="tma-menu-row-sub">{immutable ? 'View the approved version in the web app' : 'The full editor with every tool, signed in as you'}</span>
          </span>
        </button> : null}
        {!documentReadOnly ? <p className="tma-doc-field-hint">
          Tap any module row in the document to edit it, or use “+ Add module”
          at the end of a case.
        </p> : null}
      </Sheet>

      {/* Module editor — the mobile equivalent of the web document editor */}
      <Sheet
        open={!documentReadOnly && !!draft}
        onClose={() => { if (!savingSpec) requestLeave(() => setDraft(null)); }}
        title={draft?.moduleIdx == null ? 'Add module' : 'Edit module'}
        headerAction={
          <button
            type="button"
            className="tma-sheet-text-btn"
            disabled={savingSpec}
            onClick={() => requestLeave(() => setDraft(null))}
          >
            Cancel
          </button>
        }
        footer={
          <div className="tma-doc-sheet-actions">
            {draft && draft.moduleIdx != null ? (
              <button
                type="button"
                className="tma-doc-btn tma-doc-btn-danger"
                disabled={savingSpec}
                onClick={() => deleteModule(draft.caseIdx, draft.moduleIdx as number)}
              >
                Delete
              </button>
            ) : null}
            <button
              type="button"
              className="tma-doc-btn tma-doc-btn-primary tma-doc-btn-grow"
              disabled={savingSpec || !draftValid || caseFull}
              onClick={saveDraft}
            >
              {savingSpec ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        {draft ? (
          <div className="tma-doc-form">
            {viewerError ? <div className="tma-doc-banner err" role="alert">{viewerError}</div> : null}
            <div className="tma-doc-form-case">
              {(draftCase?.name || 'Case ' + (draft.caseIdx + 1)).trim()}
            </div>

            <label className="tma-doc-field-label" htmlFor="tma-mod-name">
              Module
            </label>
            <input
              id="tma-mod-name"
              className="tma-doc-input tma-doc-input-block"
              type="text"
              maxLength={CAP_MODULE_NAME}
              placeholder="e.g. Client onboarding form"
              value={draft.name}
              disabled={savingSpec}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />

            <label className="tma-doc-field-label" htmlFor="tma-mod-desc">
              What it covers
            </label>
            <textarea
              id="tma-mod-desc"
              className="tma-doc-textarea tma-doc-textarea-sm"
              rows={3}
              maxLength={CAP_DESC}
              placeholder="One or two sentences the client will read"
              value={draft.description}
              disabled={savingSpec}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />

            <label className="tma-doc-field-label" htmlFor="tma-mod-days">
              Days
            </label>
            <div className="tma-doc-days-row">
              <input
                id="tma-mod-days"
                className={
                  'tma-doc-input tma-doc-input-days' +
                  (parseDays(draft.daysRaw) == null ? ' bad' : '')
                }
                type="text"
                inputMode="text"
                aria-invalid={parseDays(draft.daysRaw) == null || undefined}
                aria-describedby="tma-mod-days-hint"
                placeholder="2 or 1–2"
                value={draft.daysRaw}
                disabled={savingSpec}
                onChange={(e) => setDraft({ ...draft, daysRaw: e.target.value })}
              />
              {[
                { label: 'TBD', value: 'TBD' },
                { label: 'N/A', value: '–' },
              ].map((q) => (
                <button
                  key={q.value}
                  type="button"
                  className={
                    'tma-doc-quick' + (draft.daysRaw.trim() === q.value ? ' active' : '')
                  }
                  disabled={savingSpec}
                  onClick={() => {
                    haptic.select();
                    setDraft({ ...draft, daysRaw: q.value });
                  }}
                >
                  {q.label}
                </button>
              ))}
            </div>
            <p className="tma-doc-field-hint" id="tma-mod-days-hint">
              0.5–60 days in half-day steps, a range, TBD, or N/A. Decimal commas work too.
            </p>

            {isMarker(parseDays(draft.daysRaw)) ? (
              <>
                <label className="tma-doc-field-label" htmlFor="tma-mod-tbd">
                  Why is it TBD / not applicable?
                </label>
                <input
                  id="tma-mod-tbd"
                  className="tma-doc-input tma-doc-input-block"
                  type="text"
                  maxLength={CAP_TBD_REASON}
                  placeholder="Needed — the document explains every open item"
                  value={draft.tbdReason}
                  disabled={savingSpec}
                  onChange={(e) => setDraft({ ...draft, tbdReason: e.target.value })}
                />
              </>
            ) : null}

            <label className="tma-doc-field-label" htmlFor="tma-mod-apis">
              Integrations (optional)
            </label>
            <input
              id="tma-mod-apis"
              className="tma-doc-input tma-doc-input-block"
              type="text"
              placeholder="Comma separated — up to 6"
              value={draft.apisRaw}
              disabled={savingSpec}
              onChange={(e) => setDraft({ ...draft, apisRaw: e.target.value })}
            />

            {caseFull ? (
              <div className="tma-doc-banner warn" role="alert">
                This case already has the maximum number of modules.
              </div>
            ) : null}
            <p className="tma-doc-field-hint">
              Saving re-renders the document and sends it back to review — any
              approvals already given for the old version are cleared.
            </p>
          </div>
        ) : null}
      </Sheet>

      {/* AI chat — Ask / Edit, with the server-persisted thread */}
      <ChatSheet
        key={viewer?.id || 'estimate-chat'}
        open={canEstimate && chatOpen && (!readOnly || immutable)}
        onClose={() => requestLeave(() => setChatOpen(false))}
        title="Lato AI"
        onDraftStateChange={setChatDraft}
        askEnabled
        editEnabled={!immutable && !readOnly}
        models={ESTIMATE_MODELS}
        model={chatModel}
        onModelChange={setChatModel}
        messages={chatMessages}
        busy={chatBusy}
        historyLoading={chatLoading}
        onSend={chatSend}
        onClear={chatClear}
        editPrompts={EDIT_PROMPTS}
        askPrompts={ASK_PROMPTS}
      />

      {/* Approve feedback drawer (estimator, review) */}
      <Sheet
        open={showApprove && feedbackOpen}
        onClose={() => requestLeave(() => setFeedbackOpen(false))}
        title="Approve estimate"
        footer={
          <button
            type="button"
            className="tma-doc-btn tma-doc-btn-primary tma-doc-btn-block"
            disabled={approving}
            onClick={approve}
          >
            {approving ? 'Approving…' : 'Approve'}
          </button>
        }
      >
        {viewerError ? <div className="tma-doc-banner err" role="alert">{viewerError}</div> : null}
        <ApprovalNote status={apprStatus} userId={user?.id} pending />
        <label className="tma-doc-field-label" htmlFor="tma-est-feedback">
          Knowledge-base feedback
        </label>
        <textarea
          id="tma-est-feedback"
          className="tma-doc-textarea"
          rows={4}
          placeholder="Optional — added to the knowledge base once the CEO approves it under “Feedback”."
          value={feedback}
          disabled={approving}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <p className="tma-doc-field-hint">
          {apprStatus && apprStatus.required > 1
            ? 'This step needs ' +
              apprStatus.required +
              ' approvals — the estimate moves on once they are all in.'
            : 'After approval the price sheet is prepared automatically for the assigned pricing owner.'}
        </p>
      </Sheet>
    </div>
  );
}

/* ═══════════ native document render (spec v2) ═══════════════════
   The server returns the estimate already wrapped to v2 with computed
   subtotals / total, so this render never computes a figure of its own —
   case-cards, a monospaced day badge per module, notes, the summary and an
   English total line. Document text (client / case / module names) is echoed
   verbatim in whatever language it was authored; only the chrome is English.

   DESIGN24 §T2.1 — when `editable`, each module row becomes a tap target
   that opens the edit sheet and every case gains an «+ Add module» row. */

interface DocProps {
  spec: EstimateSpec;
  documentId: string;
  status: string;
  editable?: boolean;
  onEditModule?: (caseIdx: number, moduleIdx: number) => void;
  onAddModule?: (caseIdx: number) => void;
  onCommentAnchor?: (anchor: CommentAnchor) => void;
  commentPins?: CommentPinRef[];
  onOpenCommentThread?: (id: string) => void;
  pickingComment?: boolean;
}

function EstimateDocument({
  spec,
  documentId,
  status,
  editable,
  onEditModule,
  onAddModule,
  onCommentAnchor,
  commentPins = [],
  onOpenCommentThread,
  pickingComment,
}: DocProps) {
  const cases = spec.cases || [];
  const notes = spec.notes || [];
  const summary = spec.summary;
  const total = spec.total;
  const revision = anchorRevision(spec);
  const makeAnchor = (path: string, label: string, quote: string): CommentAnchor => ({
    kind: 'block', document_id: documentId, revision, path,
    label: label.slice(0, 160), quote: anchorQuote(quote),
  });
  const pin = (path: string, label: string, quote: string) => {
    if (!onCommentAnchor && !onOpenCommentThread) return null;
    const anchor = makeAnchor(path, label, quote);
    const matches = commentsAtBlock(commentPins, anchor);
    if (!pickingComment && !matches.length) return null;
    return <button type="button"
      className={'tma-comment-pin' + (pickingComment ? ' is-picking' : ' has-comments')}
      aria-label={pickingComment ? `Comment on ${label}` : `Open comments on ${label}`}
      onClick={event => { event.stopPropagation(); if (!pickingComment && matches.length && onOpenCommentThread) onOpenCommentThread(matches[0].id); else onCommentAnchor?.(anchor); }}>
      {pickingComment ? <MessageSquarePlus size={16} /> : <><MessageCircle size={16} /><span className="tma-comment-pin-count">{matches.length}</span></>}
    </button>;
  };

  return (
    <div className="tma-doc">
      <div className="tma-doc-hero tma-commentable-block" data-tma-anchor-path="hero"
        onClick={pickingComment ? () => onCommentAnchor?.(makeAnchor('hero', 'Estimate overview', spec.title || spec.system || 'Estimate')) : undefined}>
        <div className="tma-doc-hero-meta">
          <div className="tma-doc-kicker">
            Development plan & timelines
            {status === 'approved' ? <span className="tma-doc-kicker-ok"> · approved</span> : null}
          </div>
          {pin('hero', 'Estimate overview', spec.title || spec.system || 'Estimate')}
        </div>
        <h2 className="tma-doc-h1">{spec.title || spec.system || 'Estimate'}</h2>
        {spec.client || spec.system ? (
          <div className="tma-doc-hero-sub">
            {[spec.client, spec.system].filter(Boolean).join(' · ')}
          </div>
        ) : null}
      </div>

      {cases.map((c, i) => (
        <CaseCard
          key={i}
          c={c}
          index={i}
          editable={!!editable}
          onEditModule={onEditModule}
          onAddModule={onAddModule}
          onCommentAnchor={onCommentAnchor}
          commentPins={commentPins}
          onOpenCommentThread={onOpenCommentThread}
          pickingComment={pickingComment}
          makeAnchor={makeAnchor}
          pin={pin}
        />
      ))}

      {notes.length ? (
        <section className="tma-doc-section">
          <h3 className="tma-doc-h3">Notes</h3>
          <ul className="tma-doc-notes">
            {notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {summary && (summary.deliverables || summary.team) ? (
        <section className="tma-doc-section tma-doc-summary">
          <h3 className="tma-doc-h3">Summary</h3>
          {summary.deliverables ? (
            <div className="tma-doc-summary-block">
              <div className="tma-doc-summary-label">Deliverables</div>
              <div className="tma-doc-summary-text">{summary.deliverables}</div>
            </div>
          ) : null}
          {summary.team ? (
            <div className="tma-doc-summary-block">
              <div className="tma-doc-summary-label">Team</div>
              <div className="tma-doc-summary-text">{summary.team}</div>
            </div>
          ) : null}
        </section>
      ) : null}

      {total ? (
        <div className="tma-doc-total">
          <div className="tma-doc-total-line">
            <span className="tma-doc-total-label">Total</span>
            <span className="tma-doc-total-val">
              {rangeText(total.min, total.max)}{' '}
              <span className="tma-doc-total-unit">days</span>
            </span>
          </div>
          <div className="tma-doc-total-sub">
            ≈ {total.min === total.max ? weekStr(total.min) : weekStr(total.min) + '–' + weekStr(total.max)} wks
            {total.excluded ? ` · excluded: ${total.excluded}` : ''}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CaseCard({
  c,
  index,
  editable,
  onEditModule,
  onAddModule,
  onCommentAnchor,
  commentPins = [],
  onOpenCommentThread,
  pickingComment,
  makeAnchor,
  pin,
}: {
  c: EstimateCase;
  index: number;
  editable: boolean;
  onEditModule?: (caseIdx: number, moduleIdx: number) => void;
  onAddModule?: (caseIdx: number) => void;
  onCommentAnchor?: (anchor: CommentAnchor) => void;
  commentPins?: CommentPinRef[];
  onOpenCommentThread?: (id: string) => void;
  pickingComment?: boolean;
  makeAnchor: (path: string, label: string, quote: string) => CommentAnchor;
  pin: (path: string, label: string, quote: string) => React.ReactNode;
}) {
  const modules = c.modules || [];
  const sub = c.subtotal;
  return (
    <section className="tma-doc-case">
      <div className="tma-doc-case-head tma-commentable-block" data-tma-anchor-path={`cases.${index}`}
        onClick={pickingComment ? () => onCommentAnchor?.(makeAnchor(`cases.${index}`, c.name || `Case ${index + 1}`, c.name || '')) : undefined}>
        <span className="tma-doc-case-idx" aria-hidden="true">
          {index + 1}
        </span>
        <span className="tma-doc-case-name">{c.name || 'Case ' + (index + 1)}</span>
        {pin(`cases.${index}`, c.name || `Case ${index + 1}`, c.name || '')}
        {sub ? (
          <span className="tma-doc-case-sub">
            {rangeText(sub.min, sub.max)} days
          </span>
        ) : null}
      </div>
      <div className="tma-doc-modules">
        {modules.map((m, i) => (
          <ModuleRow
            key={i}
            m={m}
            editable={editable}
            onEdit={onEditModule ? () => onEditModule(index, i) : undefined}
            onCommentAnchor={onCommentAnchor}
            commentPins={commentPins}
            onOpenCommentThread={onOpenCommentThread}
            pickingComment={pickingComment}
            anchor={makeAnchor(`cases.${index}.modules.${i}`, m.name || `Module ${i + 1}`, `${m.name} ${m.description}`)}
          />
        ))}
      </div>
      {editable && onAddModule ? (
        <button
          type="button"
          className="tma-doc-add-module"
          onClick={() => onAddModule(index)}
        >
          + Add module
        </button>
      ) : null}
    </section>
  );
}

function ModuleRow({
  m,
  editable,
  onEdit,
  onCommentAnchor,
  commentPins = [],
  onOpenCommentThread,
  pickingComment,
  anchor,
}: {
  m: EstimateModule;
  editable: boolean;
  onEdit?: () => void;
  onCommentAnchor?: (anchor: CommentAnchor) => void;
  commentPins?: CommentPinRef[];
  onOpenCommentThread?: (id: string) => void;
  pickingComment?: boolean;
  anchor: CommentAnchor;
}) {
  const d = dayCell(m.days);
  const apis = (m.apis || []).filter((a) => a && a.trim());
  const body = (
    <>
      <div className="tma-doc-module-main">
        <div className="tma-doc-module-name">{m.name}</div>
        {m.description ? (
          <div className="tma-doc-module-desc">{m.description}</div>
        ) : null}
        {apis.length ? (
          <div className="tma-doc-module-apis">
            {apis.map((a, i) => (
              <span className="tma-doc-api" key={i}>
                {a}
              </span>
            ))}
          </div>
        ) : null}
        {(d.kind === 'tbd' || d.kind === 'na') && m.tbd_reason ? (
          <div className="tma-doc-module-tbd">{m.tbd_reason}</div>
        ) : null}
      </div>
      <div className={'tma-doc-days ' + d.kind} aria-label="days">
        {d.text}{d.kind === 'num' || d.kind === 'range' ? <span className="tma-doc-days-unit"> days</span> : null}
      </div>
      {editable ? (
        <span className="tma-doc-module-edit" aria-hidden="true">
          ✎ <span>Edit</span>
        </span>
      ) : null}
    </>
  );

  const matches = commentsAtBlock(commentPins, anchor);
  return (
    <div className="tma-estimate-module-wrap tma-commentable-block" data-tma-anchor-path={anchor.path}
      onClick={pickingComment ? () => onCommentAnchor?.(anchor) : undefined}>
      {editable && onEdit ? <button type="button" className="tma-doc-module tma-doc-module-tap"
        onClick={pickingComment ? undefined : onEdit} aria-label={'Edit ' + (m.name || 'module')}>
        {body}</button> : <div className="tma-doc-module">{body}</div>}
      {(onCommentAnchor || onOpenCommentThread) && (pickingComment || matches.length) ? <button type="button" className={'tma-comment-pin' + (pickingComment ? ' is-picking' : ' has-comments')}
        aria-label={pickingComment ? `Comment on ${m.name || 'module'}` : `Open comments on ${m.name || 'module'}`}
        onClick={event => { event.stopPropagation(); if (!pickingComment && matches.length && onOpenCommentThread) onOpenCommentThread(matches[0].id); else onCommentAnchor?.(anchor); }}>
        {pickingComment ? <MessageSquarePlus size={16} /> : <><MessageCircle size={16} /><span className="tma-comment-pin-count">{matches.length}</span></>}</button> : null}
    </div>
  );
}

export default EstimateTab;
